// @file Whiteboard Card Templates store.
import { trackEvent } from '@@/bits/analytics'
import { captureFetchException, captureMessage } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { getTldrawFontId } from '@@/bits/whiteboard'
import { getLinkingFromShape } from '@@/bits/whiteboard_linking'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardFrameBackgroundStore } from '@@/pinia/whiteboard_frame_background'
import { useWhiteboardModeStore } from '@@/pinia/whiteboard_mode'
import { useWhiteboardPanelsStore } from '@@/pinia/whiteboard_panels'
import { WhiteboardCardTemplatesApi } from '@@/surface/api/whiteboard_card_templates'
import type { WhiteboardCardTemplate } from '@@/types'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { toRichText } from '@tldraw/editor'
import { until } from '@vueuse/core'
import { omit, pick, shuffle } from 'es-toolkit'
import { defineStore } from 'pinia'
import type { JsonObject, TLRecord, TLShapeId } from 'tldraw'
import { computed, nextTick, ref, shallowRef } from 'vue'

interface CardTemplateItem {
  id: string
  name: string
  category: string
  thumbnailUrl: string
  previewUrl: string
  searchKeywords: string[]
  trackingId: string
}

export const useWhiteboardCardTemplatesStore = defineStore('whiteboardCardTemplates', () => {
  const whiteboardStore = useWhiteboardStore()
  const whiteboardModeStore = useWhiteboardModeStore()
  const whiteboardFrameBackgroundStore = useWhiteboardFrameBackgroundStore()
  const whiteboardPanelsStore = useWhiteboardPanelsStore()
  const surfaceContainerSizeStore = useSurfaceContainerSizeStore()

  // #region Suggested card templates row
  const xSuggestedCardTemplatesRow = ref(false)
  const xStartFromATemplateButton = ref(false)

  const isEmptyWhiteboard = ref(false)
  useTldrawEditor().react((editor) => {
    isEmptyWhiteboard.value = editor.getPages().length === 1 && editor.getCurrentPageShapeIds().size === 0
  })

  // Only show the suggested templates UI if the current page has no shapes.
  const doesCurrentPageHaveShapes = ref(false)
  useTldrawEditor().react((editor) => {
    doesCurrentPageHaveShapes.value = editor.getCurrentPageShapeIds().size > 0
    if (!doesCurrentPageHaveShapes.value) {
      xSuggestedCardTemplatesRow.value = true
      xStartFromATemplateButton.value = true
    }
  })

  const canShowCardTemplatesUI = computed<boolean>(() => {
    // Only show card templates UI on certain backgrounds.
    const currentFrameBackground =
      whiteboardStore.currentFrameId != null
        ? whiteboardFrameBackgroundStore.displayedFrameBackgrounds[whiteboardStore.currentFrameId]?.id
        : null
    const canShowOnCurrentBackground =
      currentFrameBackground != null
        ? ['Plain', 'Ruled', 'DotGrid', 'Grid', 'Frosted'].includes(currentFrameBackground)
        : true
    return (
      isAppUsing('sandboxCardTemplates') &&
      cardTemplates.value.length > 0 &&
      !doesCurrentPageHaveShapes.value &&
      !whiteboardModeStore.isReadOnlyMode &&
      whiteboardModeStore.isDrawMode &&
      !whiteboardFrameBackgroundStore.isPreviewingFrameBackgrounds &&
      canShowOnCurrentBackground &&
      !whiteboardPanelsStore.xCardTemplatesPanel &&
      previewCardTemplateUrl.value == null
    )
  })

  const canShowSuggestedCardTemplates = computed<boolean>(() => {
    return (
      canShowCardTemplatesUI.value &&
      xSuggestedCardTemplatesRow.value &&
      !surfaceContainerSizeStore.isContainerSmallerThanTabletLandscape &&
      isEmptyWhiteboard.value
    )
  })

  const canShowStartFromATemplateButton = computed<boolean>(() => {
    return (
      canShowCardTemplatesUI.value &&
      xStartFromATemplateButton.value &&
      (surfaceContainerSizeStore.isContainerSmallerThanTabletLandscape || !isEmptyWhiteboard.value)
    )
  })

  const openCardTemplatesPanel = (): void => {
    whiteboardPanelsStore.xCardTemplatesPanel = true
    dismissCardTemplatesSuggestionUI()
  }

  const dismissCardTemplatesSuggestionUI = (): void => {
    xSuggestedCardTemplatesRow.value = false
    xStartFromATemplateButton.value = false
  }
  // #endregion

  // #region Card template list
  const cardTemplates = shallowRef<CardTemplateItem[]>([])
  const cardTemplatesFetchState = ref<'idle' | 'fetching' | 'success' | 'error'>('idle')
  const cardTemplateSnapshotsById = shallowRef<Record<string, WhiteboardCardTemplate['page_snapshot']>>({})

  const suggestedCardTemplates = computed<CardTemplateItem[]>(() => {
    return shuffle([...cardTemplates.value]).slice(0, 8)
  })

  const updateCardTemplates = (rawCardTemplates: WhiteboardCardTemplate[]): void => {
    cardTemplates.value = rawCardTemplates.map((template) => ({
      id: template.id,
      name: template.translated_name,
      category: template.translated_category,
      thumbnailUrl: template.thumbnail_url,
      previewUrl: template.preview_url,
      searchKeywords: template.translated_search_keywords,
      trackingId: template.name, // Use the original template name as the tracking ID
    }))
    cardTemplateSnapshotsById.value = rawCardTemplates.reduce((acc, template) => {
      acc[template.id] = template.page_snapshot
      return acc
    }, {})
  }

  const fetchAndSaveCardTemplatesToStore = async (): Promise<void> => {
    if (['fetching', 'success'].includes(cardTemplatesFetchState.value)) return

    try {
      cardTemplatesFetchState.value = 'fetching'
      const rawCardTemplates = await WhiteboardCardTemplatesApi.fetch({ wallId: surfaceStore.wallId })
      updateCardTemplates(rawCardTemplates)
      cardTemplatesFetchState.value = 'success'
    } catch (e) {
      captureFetchException(e, { source: 'fetchAndSaveCardTemplatesToStore' })
      cardTemplatesFetchState.value = 'error'
    }
  }
  // #endregion

  // #region Preview card template
  const isTemplatePreviewUIAnimating = ref(false)

  const isShowingCardTemplatePreview = computed<boolean>(
    // `isTemplatePreviewUIAnimating` becomes true whenever `xCardTemplatesPanel` changes and turns to false again
    // after the animation is done. When the templates panel is closed, we wait for the animation before disabling
    // the preview UI to minimize Tldraw's resize flickering.
    //
    // Timeline presentation:
    //   xCardTemplatesPanel             true                     false
    //   isTemplatePreviewUIAnimating    true  ->  false          true  ->  false
    //                                   [1]                                [2]
    //
    // [1] Start showing card template preview
    // [2] Stop showing card template preview
    () => whiteboardPanelsStore.xCardTemplatesPanel || isTemplatePreviewUIAnimating.value,
  )

  const previewCardTemplateId = ref<string | null>(null)

  const previewCardTemplateUrl = computed<string | null>(() => {
    if (previewCardTemplateId.value == null) return null
    return cardTemplates.value.find((template) => template.id === previewCardTemplateId.value)?.previewUrl ?? null
  })

  const setPreviewTemplate = (templateId: string): void => {
    previewCardTemplateId.value = templateId
  }

  const stopPreviewingTemplates = async (): Promise<void> => {
    // Setting this to false triggers the animation to close the preview UI.
    whiteboardPanelsStore.xCardTemplatesPanel = false

    // But we need to keep the `previewCardTemplateId` state until the animation
    // is done because during the animation `WhiteboardCardTemplatePreview` is
    // still being shown.
    await nextTick()
    if (isTemplatePreviewUIAnimating.value) {
      await until(isTemplatePreviewUIAnimating).toBe(false)
    }

    // Once the animation completes, we can unset this state now so `WhiteboardFrameBackground`
    // takes over the job of rendering the card background.
    previewCardTemplateId.value = null
  }
  // #endregion

  // #region Apply card template
  const surfaceStore = useSurfaceStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const isApplyingCardTemplate = ref(false)

  /**
   * Applies card template to the current card.
   * If `templateId` is not provided, it will use the current preview template.
   * If there is no preview template, it will do nothing.
   */
  const applyCardTemplate = async (templateId?: CardTemplateItem['id']): Promise<void> => {
    const editor = useTldrawEditor().editor
    if (editor == null) return

    const chosenTemplateId = templateId ?? previewCardTemplateId.value
    if (chosenTemplateId == null) {
      return
    }

    const templateSnapshot = cardTemplateSnapshotsById.value[chosenTemplateId]
    if (templateSnapshot == null) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: __('Something went wrong. The card template is not available.'),
      })
      captureMessage('[WhiteboardCardTemplates] The card template is not available', {
        context: {
          wallId: surfaceStore.wallId,
          templateId: chosenTemplateId,
        },
      })
      return
    }

    isApplyingCardTemplate.value = true
    const currentPageId = editor.getCurrentPageId()
    editor.updatePage({
      id: currentPageId,
      meta: pick(templateSnapshot.page.meta, ['backgroundType', 'customBackgroundUrl']),
    })

    const newShapeIdsMap = new Map<TLShapeId, TLShapeId>()
    const { createBindingId, createShapeId, isPageId } = await import('tldraw')

    const processedRecords = templateSnapshot.records
      .map((record) => {
        // Generate new shape IDs for the template shapes.
        // We can't use the same shape IDs because they might conflict with existing shapes.
        if (record.typeName === 'shape') {
          newShapeIdsMap.set(record.id, createShapeId())
        }
        return record
      })
      .map((record) => {
        if (record.typeName === 'shape') {
          // If parent ID of shape is a page, change it to the current page ID.
          // Otherwise, use the new shape ID generated above.
          let newParentId = record.parentId
          if (isPageId(newParentId)) {
            newParentId = currentPageId
          } else {
            newParentId = newShapeIdsMap.get(newParentId) ?? newParentId
          }

          const newShapeId = newShapeIdsMap.get(record.id) ?? record.id

          // We only keep URL linking for now. Other linking types are relative and should
          // not be preserved when applying a card template.
          const linking = getLinkingFromShape(record)
          const newMeta: Pick<JsonObject, 'approved' | 'sound_effect' | 'visual_effect'> & { linking?: string } = pick(
            record.meta,
            ['approved', 'sound_effect', 'visual_effect'],
          )
          if (linking?.targetType === 'url') {
            newMeta.linking = linking.toString()
          }

          let newProps = record.props
          if (isAppUsing('sandboxTextFonts')) {
            const tldrawFontId = getTldrawFontId(surfaceStore.fontId)
            if (tldrawFontId != null && 'font' in record.props) {
              newProps = {
                ...newProps,
                font: tldrawFontId,
              }
            }
          }

          // Convert text content to rich text if the shape has text content.
          // From tldraw v3.10, text content is stored as rich text in `richText` prop for shapes below
          const isShapeWithRichText = ['text', 'padlet-speech-bubble', 'note', 'geo'].includes(record.type)
          if (isShapeWithRichText && 'text' in newProps) {
            newProps = {
              ...omit(newProps, ['text']),
              richText: toRichText(newProps.text ?? ''),
            }
          }

          return {
            ...omit(record, ['meta']),
            props: newProps,
            meta: newMeta,
            parentId: newParentId,
            id: newShapeId,
          }
        } else if (record.typeName === 'binding') {
          // For bindings, update from/to IDs to the new shape IDs generated above.
          return {
            ...record,
            id: createBindingId(),
            fromId: newShapeIdsMap.get(record.fromId) ?? record.fromId,
            toId: newShapeIdsMap.get(record.toId) ?? record.toId,
          }
        } else if (record.typeName === 'asset') {
          // For assets, we return them as-is.
          return record
        }
        return null
      })
      .filter(Boolean) as Array<NonNullable<TLRecord>>

    // We need to wait until the animation is complete before applying the template.
    // During the animation, the screenshot of the card template is still being shown.
    // If we apply the template too soon, the actual shapes rendered by Tldraw will
    // overlay the template screenshot.
    await stopPreviewingTemplates()
    editor.store.put(processedRecords)

    // Reset tool to default tool after applying template.
    whiteboardStore.selectDefaultTool()
    isApplyingCardTemplate.value = false

    const appliedTemplate = cardTemplates.value.find((template) => template.id === chosenTemplateId)
    trackEvent('Whiteboard', 'Applied card template', appliedTemplate?.trackingId)
  }
  // #endregion

  return {
    xSuggestedCardTemplatesRow,
    xStartFromATemplateButton,
    canShowSuggestedCardTemplates,
    canShowStartFromATemplateButton,
    openCardTemplatesPanel,
    dismissCardTemplatesSuggestionUI,

    cardTemplates,
    cardTemplatesFetchState,
    cardTemplateSnapshotsById,
    suggestedCardTemplates,
    fetchAndSaveCardTemplatesToStore,

    isTemplatePreviewUIAnimating,
    isShowingCardTemplatePreview,
    previewCardTemplateId,
    previewCardTemplateUrl,
    setPreviewTemplate,
    stopPreviewingTemplates,

    isApplyingCardTemplate,
    applyCardTemplate,
  }
})

export type { CardTemplateItem }
